import { LegalPageLayout } from './LegalPageLayout';

const LAST_UPDATED = 'May 26, 2026';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-to-request', label: 'How to request deletion' },
  { id: 'what-to-include', label: 'What to include' },
  { id: 'scope', label: 'Deletion scope' },
  { id: 'status-confirmation', label: 'Status and confirmation' },
  { id: 'meta-removal', label: 'Meta app removal' },
  { id: 'settings-url', label: 'Meta settings URL' },
];

export function DataDeletion() {
  return (
    <LegalPageLayout
      title="Data Deletion"
      description="Instructions for requesting deletion of AdVerdict account, team, project, creative, Meta import, and analysis data."
      lastUpdated={LAST_UPDATED}
      sections={sections}
    >
      <section id="overview" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">Overview</h2>
        <p>
          This page explains how to ask AdVerdict to delete your data, including
          account, team, project, creative, Meta import, metrics, analysis, and
          support/deletion request records.
        </p>
      </section>

      <section id="how-to-request" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          How to request deletion
        </h2>
        <p>
          Send a deletion request to Hendrik Wels at{' '}
          <a
            className="break-words text-brand hover:underline"
            href="mailto:hendrik.wels1@gmail.com"
          >
            hendrik.wels1@gmail.com
          </a>
          . State that you want AdVerdict to delete your data and identify
          whether the request concerns an account, team, project, creative, Meta
          connection, imported Meta asset, analysis output, or support record.
        </p>
      </section>

      <section id="what-to-include" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          What to include
        </h2>
        <p>
          Include the email address used for AdVerdict, your team or workspace
          name, relevant project or creative names, any Meta ad account or Page
          identifiers you can safely provide, and enough context for AdVerdict
          to verify your authority over the requested records. Do not send Meta
          access tokens, passwords, cookies, or private keys.
        </p>
      </section>

      <section id="scope" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">Deletion scope</h2>
        <p>
          After verification, AdVerdict expects to remove or de-identify data
          that is within the verified account, team, project, creative, Meta
          import, analysis, or support/deletion request scope, subject to
          security, legal, accounting, dispute, audit, and platform-policy
          retention requirements. Disconnecting Meta does not automatically
          define deletion of already imported creative or metrics data. Project
          soft deletes are recoverable for 30 days before purge where the
          current product workflow applies. Meta tokens and connection metadata
          are removed or revoked on disconnect or deletion according to the
          approved workflow.
        </p>
      </section>

      <section id="status-confirmation" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Status and confirmation
        </h2>
        <p>
          AdVerdict will acknowledge deletion requests, verify the requester,
          review the records in scope, perform approved deletion or explain any
          retained records, and provide confirmation when the request is
          completed. Support, deletion, security, and operational records are
          kept only as long as reasonably needed for handling, audit, security,
          and legal purposes.
        </p>
      </section>

      <section id="meta-removal" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Meta app removal
        </h2>
        <p>
          If you remove the AdVerdict app from Meta, you may still need to send
          a deletion request to AdVerdict so the team can identify and process
          data previously imported or generated from your authorized Meta
          connection. Meta app removal does not by itself prove which AdVerdict
          team, project, or creative records should be deleted.
        </p>
      </section>

      <section id="settings-url" className="space-y-3">
        <h2 className="text-xl font-semibold text-primary">
          Meta Basic Settings URL
        </h2>
        <p>
          User Data Deletion URL for Meta Basic Settings:{' '}
          <a
            className="break-words text-brand hover:underline"
            href="https://adverdict.co/data-deletion"
          >
            https://adverdict.co/data-deletion
          </a>
          .
        </p>
      </section>
    </LegalPageLayout>
  );
}

export default DataDeletion;
